import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';

import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image';
import {
  AspectRatio,
  Box,
  useBreakpointValue,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import GalleryVideoSlide from '@amblique/king-living/app/components/gallery-video-slide';
import {Swiper} from '@amblique/king-living/app/components/shared/swiper';
import {
  PRODUCT_TILE_CAROUSEL_SLIDE_IMAGE_NAVIGATION,
  PRODUCT_TILE_IMAGE_NAVIGATION,
} from '@amblique/king-living/app/constants/swiper-options';
import {
  getProductTileImageMode,
  shouldAllowProductTileSwiperTouch,
  shouldEnableProductTileSwiperLoop,
  shouldShowProductTileSwiperNavigation,
  shouldUseProductTileSwiper,
} from '@amblique/king-living/app/constants/product-tile-image-behavior';
import {PRODUCT_TILE_VIDEO_CONTAINER} from '@amblique/king-living/app/theme/components/project/product-tile-image-theme';
import {isTileVideoMedia} from '@amblique/king-living/app/utils/product-images';

/**
 * Shared hero image renderer for product tiles (Range, Instock, Contentful grid, upsell, blog).
 */
const ProductTileImageGallery = ({
  images = [],
  aspectRatioProps,
  swiperContainerProps,
  hoverSwapWrapperProps,
  hoverSwapLayerProps,
  hoverSwapPrimaryProps,
  hoverSwapSecondaryProps,
  imageWidths,
  buildSrc,
  getAlt,
  getLoading,
  placeholder = null,
  swiperPreset = 'range_tile',
  isInCarouselSlide = false,
  staticHeroImage = false,
}) => {
  const isMobile = useBreakpointValue({base: true, md: false});
  const imageCount = images.length;
  const hasVideoSlides = images.some(isTileVideoMedia);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  const syncActiveSlideFromSwiper = useCallback((swiper) => {
    const nextReal =
      typeof swiper?.realIndex === 'number' ?
        swiper.realIndex :
        typeof swiper?.activeIndex === 'number' ?
          swiper.activeIndex :
          0;

    setActiveSlideIndex(nextReal);
  }, []);

  useEffect(() => {
    if (hasVideoSlides) {
      setActiveSlideIndex(0);
    }
  }, [images, hasVideoSlides]);

  // Emit explicit `width`/`height` HTML *attributes* on each tile <img>, derived from the reserved
  // `<AspectRatio>` box. This satisfies the TSD "images have explicit height/width" requirement
  // (and the matching Lighthouse "image elements do not have explicit width and height" audit)
  // without touching the responsive `width/height: 100%` CSS that actually sizes the image inside
  // its container. Only the *ratio* of the two numbers matters to the browser here (both CSS
  // dimensions are constrained by the AspectRatio parent), so we express the reserved ratio as
  // integers on a fixed height basis rather than shipping intrinsic pixel sizes per image — the
  // Cylindo/PIM feed doesn't give us reliable per-image intrinsic dimensions anyway. Falls back to
  // no attributes when the consumer passes a responsive (object) ratio we can't reduce to a scalar.
  const htmlDimensionProps = useMemo(() => {
    const ratio = aspectRatioProps?.ratio;

    if (typeof ratio !== 'number' || !Number.isFinite(ratio) || ratio <= 0) return {};

    const HEIGHT_BASIS = 1000;

    return {htmlWidth: Math.round(ratio * HEIGHT_BASIS), htmlHeight: HEIGHT_BASIS};
  }, [aspectRatioProps?.ratio]);

  const mode = useMemo(
    () => getProductTileImageMode(imageCount, {
      isMobile,
      isInCarouselSlide,
      staticHeroOnly: staticHeroImage,
    }),
    [imageCount, isMobile, isInCarouselSlide, staticHeroImage]
  );

  const useVideoSafeSwiper = hasVideoSlides && shouldUseProductTileSwiper(mode);

  const swiperOptions = useMemo(() => {
    const showNavigation = shouldShowProductTileSwiperNavigation(mode, {isMobile, isInCarouselSlide});
    const allowTouchMove = shouldAllowProductTileSwiperTouch({isInCarouselSlide});
    const tileNavigation = isInCarouselSlide ?
      PRODUCT_TILE_CAROUSEL_SLIDE_IMAGE_NAVIGATION :
      PRODUCT_TILE_IMAGE_NAVIGATION;
    const shared = {
      navigation: showNavigation ? tileNavigation : false,
      ...(hasVideoSlides ? {onSlideChange: syncActiveSlideFromSwiper} : {}),
    };

    if (isInCarouselSlide) {
      return {...shared, loop: false, rewind: true, allowTouchMove: false};
    }

    return {
      ...shared,
      loop: useVideoSafeSwiper ?
        false :
        shouldEnableProductTileSwiperLoop() && imageCount >= 2,
      rewind: useVideoSafeSwiper,
      allowTouchMove,
    };
  }, [mode, isMobile, isInCarouselSlide, imageCount, hasVideoSlides, useVideoSafeSwiper, syncActiveSlideFromSwiper]);

  const renderTileMedia = (image, index, {isVideoActive = true, slideKey} = {}) => {
    if (isTileVideoMedia(image)) {
      return (
        <GalleryVideoSlide
          key={slideKey}
          alt={getAlt(image, index)}
          containerProps={PRODUCT_TILE_VIDEO_CONTAINER}
          isActive={isVideoActive}
          isMuted={true}
          showControls={false}
          videoUrl={image.videoUrl}
        />
      );
    }

    return (
      <DynamicImage
        key={slideKey}
        src={buildSrc(image, index)}
        widths={imageWidths}
        imageProps={{
          alt: getAlt(image, index),
          loading: getLoading(index),
          objectFit: 'contain',
          width: '100%',
          height: '100%',
          ...htmlDimensionProps,
        }}
      />
    );
  };

  if (!imageCount) {
    return placeholder ? <AspectRatio {...aspectRatioProps}>{placeholder}</AspectRatio> : null;
  }

  if (mode === 'single') {
    const image = images[0];

    return (
      <AspectRatio {...aspectRatioProps}>
        {renderTileMedia(image, 0)}
      </AspectRatio>
    );
  }

  if (mode === 'hover-swap') {
    const [primaryImage, secondaryImage] = images;

    return (
      <AspectRatio {...aspectRatioProps}>
        <Box
          data-testid="product-tile-image-hover-swap"
          {...hoverSwapWrapperProps}
        >
          <Box
            className="product-tile-image__hover-primary"
            {...hoverSwapLayerProps}
            {...hoverSwapPrimaryProps}
          >
            {renderTileMedia(primaryImage, 0)}
          </Box>
          <Box
            className="product-tile-image__hover-secondary"
            {...hoverSwapLayerProps}
            {...hoverSwapSecondaryProps}
          >
            {renderTileMedia(secondaryImage, 1)}
          </Box>
        </Box>
      </AspectRatio>
    );
  }

  if (shouldUseProductTileSwiper(mode)) {
    const activeSwiperPreset = isInCarouselSlide ? 'range_tile_carousel_slide' : swiperPreset;

    return (
      <AspectRatio {...aspectRatioProps}>
        <Box {...swiperContainerProps}>
          <Swiper
            options={swiperOptions}
            presets={activeSwiperPreset}
            onSwiper={hasVideoSlides ? syncActiveSlideFromSwiper : undefined}
          >
            {images.map((image, index) => renderTileMedia(image, index, {
              isVideoActive: activeSlideIndex === index,
              slideKey: `product-tile-image-${index}`,
            }))}
          </Swiper>
        </Box>
      </AspectRatio>
    );
  }

  return null;
};

ProductTileImageGallery.displayName = 'ProductTileImageGallery';
ProductTileImageGallery.propTypes = {
  images: PropTypes.arrayOf(PropTypes.object),
  aspectRatioProps: PropTypes.object.isRequired,
  swiperContainerProps: PropTypes.object,
  hoverSwapWrapperProps: PropTypes.object,
  hoverSwapLayerProps: PropTypes.object,
  hoverSwapPrimaryProps: PropTypes.object,
  hoverSwapSecondaryProps: PropTypes.object,
  imageWidths: PropTypes.object.isRequired,
  buildSrc: PropTypes.func.isRequired,
  getAlt: PropTypes.func.isRequired,
  getLoading: PropTypes.func.isRequired,
  placeholder: PropTypes.node,
  swiperPreset: PropTypes.string,
  isInCarouselSlide: PropTypes.bool,

  /** When true, always render the first image only (no inner Swiper). */
  staticHeroImage: PropTypes.bool,
};

export default ProductTileImageGallery;
